// ---------------------------------------------------------------------------
// Question library — combines all question sets and supplements
// ---------------------------------------------------------------------------

import type { Question } from '../types';
import { acordBaseQuestions } from './acord-base';
import { coverageSelectQuestions } from './coverage-select';
import { artisanContractorQuestions } from './supplements/artisan-contractor';
import { autoFleetQuestions } from './supplements/auto-fleet';
import { autoServiceQuestions } from './supplements/auto-service';
import { buildersRiskQuestions } from './supplements/builders-risk';
import { cyberHabitationalQuestions } from './supplements/cyber-habitational';
import { documentsChecklistQuestions } from './supplements/documents-checklist';
import { environmentalQuestions } from './supplements/environmental';
import { epliFairHousingQuestions } from './supplements/epli-fairhousing';
import { equipmentBreakdownQuestions } from './supplements/equipment-breakdown';
import { generalContractorQuestions } from './supplements/general-contractor';
import { glHabitationalQuestions } from './supplements/gl-habitational';
import { habitationalQuestions } from './supplements/habitational';
import { homeHealthQuestions } from './supplements/home-health';
import { lossRunsQuestions } from './supplements/loss-runs';
import { manufacturedHousingQuestions } from './supplements/manufactured-housing';
import { manufacturingQuestions } from './supplements/manufacturing';
import { operationsProfileQuestions } from './supplements/operations-profile';
import { propertyLobQuestions } from './supplements/property-lob';
import { restaurantQuestions } from './supplements/restaurant';
import { umbrellaQuestions } from './supplements/umbrella';
import { wcMultistateQuestions } from './supplements/wc-multistate';

// Re-export individual sets
export { acordBaseQuestions } from './acord-base';
export { coverageSelectQuestions } from './coverage-select';
export { artisanContractorQuestions } from './supplements/artisan-contractor';
export { generalContractorQuestions } from './supplements/general-contractor';
export { restaurantQuestions } from './supplements/restaurant';
export { autoServiceQuestions } from './supplements/auto-service';
export { manufacturingQuestions } from './supplements/manufacturing';
export { homeHealthQuestions } from './supplements/home-health';
// MHMF (habitational / manufactured housing) family
export { manufacturedHousingQuestions } from './supplements/manufactured-housing';
export { habitationalQuestions } from './supplements/habitational';
export { operationsProfileQuestions } from './supplements/operations-profile';
export { documentsChecklistQuestions } from './supplements/documents-checklist';
export { propertyLobQuestions } from './supplements/property-lob';
export { glHabitationalQuestions } from './supplements/gl-habitational';
export { umbrellaQuestions } from './supplements/umbrella';
export { epliFairHousingQuestions } from './supplements/epli-fairhousing';
export { wcMultistateQuestions } from './supplements/wc-multistate';
export { autoFleetQuestions } from './supplements/auto-fleet';
export { equipmentBreakdownQuestions } from './supplements/equipment-breakdown';
export { environmentalQuestions } from './supplements/environmental';
export { cyberHabitationalQuestions } from './supplements/cyber-habitational';
export { buildersRiskQuestions } from './supplements/builders-risk';
export { lossRunsQuestions } from './supplements/loss-runs';

// Re-export taxonomy + state rules
export { industryTaxonomy } from './industry-taxonomy';
export { stateRules, getStateRule } from './state-rules';

/**
 * Mark a question set agent-mode-only.
 *
 * Per Centered's revised document-first intake (2026-06-11), the prospect
 * flow gathers detailed LOB data from uploaded documents (expiring policies,
 * loss runs, rent roll, X-Mod worksheet, vehicle/driver lists) instead of
 * asking for it. These question sets stay in the library for the agent
 * intake — gap-filling after document review and the client call.
 */
const agentOnly = (questions: Question[]): Question[] =>
  questions.map((q) => (q.modes ? q : { ...q, modes: ['agent'] as Question['modes'] }));

const agentLobQuestions: Record<string, Question[]> = {
  property: agentOnly(propertyLobQuestions),
  gl_bop: agentOnly(glHabitationalQuestions),
  umbrella: agentOnly(umbrellaQuestions),
  epli: agentOnly(epliFairHousingQuestions),
  workers_comp: agentOnly(wcMultistateQuestions),
  commercial_auto: agentOnly(autoFleetQuestions),
  equipment_breakdown: agentOnly(equipmentBreakdownQuestions),
  environmental: agentOnly(environmentalQuestions),
  cyber_crime: agentOnly(cyberHabitationalQuestions),
  builders_risk: agentOnly(buildersRiskQuestions),
  loss_runs: agentOnly(lossRunsQuestions),
};

/**
 * The complete question library, in sort order.
 * Frontend engine receives this and filters by conditions + mode.
 */
export const allQuestions: Question[] = [
  ...acordBaseQuestions,
  ...coverageSelectQuestions,
  ...artisanContractorQuestions,
  ...generalContractorQuestions,
  ...restaurantQuestions,
  ...autoServiceQuestions,
  ...manufacturingQuestions,
  ...homeHealthQuestions,
  // MHMF family — prospect-facing (mirrors the revised short intake)
  ...manufacturedHousingQuestions,
  ...habitationalQuestions,
  ...operationsProfileQuestions,
  ...documentsChecklistQuestions,
  // MHMF family — agent-mode LOB detail (filled from documents + the call)
  ...Object.values(agentLobQuestions).flat(),
];

/**
 * Supplement registry — maps supplement key to question set.
 * Used by the engine to load only the questions for the selected industry.
 */
export const supplementRegistry: Record<string, Question[]> = {
  artisan_contractor: artisanContractorQuestions,
  general_contractor: generalContractorQuestions,
  restaurant: restaurantQuestions,
  auto_service: autoServiceQuestions,
  manufacturing: manufacturingQuestions,
  home_health: homeHealthQuestions,
  manufactured_housing: manufacturedHousingQuestions,
  habitational: habitationalQuestions,
};

/**
 * Line-of-coverage supplement registry — keyed by the coverage_lines value
 * that activates each set. Agent-mode only (see agentOnly above).
 */
export const lobSupplementRegistry: Record<string, Question[]> = agentLobQuestions;
